import React, { useState, useCallback, useMemo } from "react";
import {
  MultiSelect,
  MultiSelectChangeEvent,
  MultiSelectFilterChangeEvent,
  MultiSelectTagData,
} from "@progress/kendo-react-dropdowns";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import styles from "./FilterBar.module.css";

export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterBarMultiSelectProps {
  options: string[] | FilterOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  maxVisibleTags?: number;
  customPrefix?: React.ReactNode;
}

// Helper to normalize options
const normalizeOptions = (
  options: string[] | FilterOption[]
): FilterOption[] => {
  if (options.length === 0) return [];
  if (typeof options[0] === "string") {
    return (options as string[]).map((opt) => ({ label: opt, value: opt }));
  }
  return options as FilterOption[];
};

export const FilterBarMultiSelect: React.FC<FilterBarMultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select...",
  className = "",
  disabled = false,
  maxVisibleTags = 2,
  customPrefix,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filterValue, setFilterValue] = useState("");

  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!filterValue) return normalizedOptions;
    const lowerFilter = filterValue.toLowerCase();
    return normalizedOptions.filter((opt) =>
      opt.label.toLowerCase().includes(lowerFilter)
    );
  }, [normalizedOptions, filterValue]);

  // Handle value change
  const handleChange = useCallback(
    (event: MultiSelectChangeEvent) => {
      const newValue = event.value.map((item: FilterOption) => item.value);
      onChange(newValue);
    },
    [onChange]
  );

  // Handle filter change
  const handleFilterChange = useCallback(
    (event: MultiSelectFilterChangeEvent) => {
      setFilterValue(event.filter.value);
    },
    []
  );

  // Convert string values to FilterOption objects for the component
  const selectedValues = useMemo(() => {
    return value
      .map((v) => normalizedOptions.find((opt) => opt.value === v))
      .filter(Boolean) as FilterOption[];
  }, [value, normalizedOptions]);

  // Calculate visible items and remaining count
  const visibleItems = selectedValues.slice(0, maxVisibleTags);
  const remainingCount = Math.max(0, selectedValues.length - maxVisibleTags);

  // Custom suffix with chevron
  const CustomSuffix = ({ expanded }: { expanded: boolean }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        paddingRight: "8px",
        color: "var(--color-text-primary, #24292f)",
      }}
    >
      {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
    </div>
  );

  // Custom prefix component
  const CustomPrefix = () => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        paddingLeft: "8px",
        color: "var(--color-text-primary, #24292f)",
      }}
    >
      {customPrefix}
    </div>
  );

  // Custom tag render function
  const tagRender = (
    tagData: MultiSelectTagData,
    li: React.ReactElement<HTMLLIElement>
  ) => {
    const item = tagData.data?.[0];

    // Handle summary tag (+N)
    if (item === "__summary__") {
      return React.cloneElement(
        li,
        {
          ...li.props,
          className: "filter-chip-summary k-chip",
        },
        <span
          style={{
            whiteSpace: "nowrap",
          }}
        >
          {tagData.text}
        </span>
      );
    }

    // Normal chip render
    const displayText =
      typeof item === "string"
        ? item
        : (item as FilterOption)?.label || tagData.text;

    const handleRemove = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const itemValue =
        typeof item === "string" ? item : (item as FilterOption)?.value;
      if (itemValue) {
        onChange(value.filter((v) => v !== itemValue));
      }
    };

    return React.cloneElement(
      li,
      {
        ...li.props,
      },
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "100px",
        }}
        title={displayText}
      >
        {displayText}
      </span>
    );
  };

  // Build tags array for rendering
  const tags = useMemo(() => {
    const result = visibleItems.map((item) => ({
      text: item.label,
      data: [item],
    }));

    // Add summary tag if there are more items
    if (remainingCount > 0) {
      result.push({
        text: `+${remainingCount}`,
        data: ["__summary__" as any],
      });
    }

    return result;
  }, [visibleItems, remainingCount]);

  return (
    <div
      className={`${styles.filterWrapper} ${className} ${
        value.length > 0 ? styles.multi_chip : ""
      }`}
    >
      <style>{`
        /* Remove background from summary chip */
        .filter-chip-summary.k-chip {
          background-color: transparent !important;
        }
      `}</style>
      <MultiSelect
        data={filteredOptions}
        textField="label"
        dataItemKey="value"
        value={selectedValues}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        filterable={true}
        onFilterChange={handleFilterChange}
        onOpen={() => setIsExpanded(true)}
        onClose={() => {
          setIsExpanded(false);
          setFilterValue("");
        }}
        tags={tags}
        tagRender={tagRender}
        autoClose={false}
        prefix={customPrefix ? () => <CustomPrefix /> : undefined}
      />
    </div>
  );
};

export default FilterBarMultiSelect;
